import { Lock } from 'lucide-react';
import useAuthStore from '@/stores/auth';
import { tenantHasFeature } from '@/utils/planFeatures';

/**
 * Route-level plan gate. Renders children when the tenant's plan has
 * the feature; otherwise a quiet upsell panel. Server middleware is the
 * real enforcement — this exists so a gated page is a pitch, not a
 * wall of failed requests.
 */
export default function PlanGate({ feature, planLabel, title, blurb, bullets = [], children }) {
  const tenant = useAuthStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);
  if (tenantHasFeature(tenant, feature)) return children;

  const isOwner = user?.role === 'owner';
  return (
    <div className="mx-auto max-w-2xl px-4 py-16">
      <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary-50">
          <Lock className="h-6 w-6 text-primary-600" />
        </div>
        <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
        <p className="mt-3 text-sm leading-6 text-gray-600">{blurb}</p>
        {bullets.length > 0 && (
          <ul className="mx-auto mt-4 max-w-md space-y-1.5 text-left text-sm text-gray-700">
            {bullets.map((b) => (
              <li key={b} className="flex gap-2">
                <span className="font-bold text-primary-600">✓</span>
                {b}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-5 text-sm font-medium text-gray-900">
          Included with the {planLabel} plan.
        </p>
        {isOwner ? (
          <a href="/settings#subscription"
            className="mt-4 inline-flex items-center rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700">
            View plans
          </a>
        ) : (
          <p className="mt-4 text-xs text-gray-500">
            Your practice owner can upgrade from Settings, Subscription.
          </p>
        )}
      </div>
    </div>
  );
}
